import type { StorefrontOrderPayloadV1 } from '@stokmerter/contracts';

import {
  buyerOrderStatusLabel,
  buyerOrderApprovedDetail,
  type BuyerOrder,
  type PageFeedback,
} from './buyer-orders-page';
import type { CatalogProduct } from './catalog-types';

type BuyerOrderLine = StorefrontOrderPayloadV1['items'][number];

export type BuyerOrderLineGroup = {
  productId: string;
  productCode: string;
  productName: string;
  lines: BuyerOrderLine[];
  totalSeries: number;
  totalExtra: number;
};

export function groupBuyerOrderLines(lines: readonly BuyerOrderLine[]): BuyerOrderLineGroup[] {
  const groups = new Map<string, BuyerOrderLineGroup>();
  for (const line of lines) {
    const key = line.productId || `${line.productCode}\u0000${line.productName}`;
    const current = groups.get(key);
    if (current) {
      current.lines.push(line);
      current.totalSeries += line.seriesQuantity;
      current.totalExtra += line.extraQuantity;
      continue;
    }
    groups.set(key, {
      productId: line.productId,
      productCode: line.productCode,
      productName: line.productName,
      lines: [line],
      totalSeries: line.seriesQuantity,
      totalExtra: line.extraQuantity,
    });
  }
  return [...groups.values()];
}

function colorHex(products: readonly CatalogProduct[], line: BuyerOrderLine): string {
  return products.find((product) => product.recordId === line.productId)?.payload.colors
    .find((color) => color.variantId === line.variantId)?.hex ?? '#d7dbe5';
}

function orderNumber(orderId: string): string {
  return orderId.slice(-10).toLocaleUpperCase('tr-TR');
}

export function BuyerOrderDetailPage(props: {
  feedback: PageFeedback | null;
  loading: boolean;
  onBack: () => void;
  onRefresh: () => void;
  order: BuyerOrder | null;
  products: CatalogProduct[];
}) {
  if (!props.order) {
    return <section className="buyer-order-detail-page">
      <div className="buyer-order-detail-heading">
        <button className="orders-back-button" onClick={props.onBack} type="button">← Geri</button>
        <h1>Sipariş Detayı</h1>
      </div>
      <div className="orders-page-state">{props.loading
        ? 'Sipariş ayrıntıları yükleniyor…'
        : 'Bu sipariş bulunamadı.'}</div>
    </section>;
  }

  const originalDetail = props.order.orderDetail;
  const detail = buyerOrderApprovedDetail(props.order);
  if (!detail) {
    return <section className="buyer-order-detail-page">
      <div className="buyer-order-detail-heading">
        <button className="orders-back-button" onClick={props.onBack} type="button">← Geri</button>
        <h1>Sipariş Detayı</h1>
      </div>
      <div className="buyer-order-detail-waiting" role="status">
        <strong>Sipariş kalemleri henüz hazır değil.</strong>
        <span>Firma panelindeki güvenli hazırlama tamamlandığında ürünler burada görünecek.</span>
        <button disabled={props.loading} onClick={props.onRefresh} type="button">
          {props.loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </div>
    </section>;
  }

  const groups = groupBuyerOrderLines(detail.items);
  const totalSeries = groups.reduce((sum, group) => sum + group.totalSeries, 0);
  const totalExtra = groups.reduce((sum, group) => sum + group.totalExtra, 0);
  const approvedByLine = new Map((props.order.decisionDetail?.outcome === 'approved'
    ? props.order.decisionDetail.lines
    : []).map((line) => [`${line.productId}:${line.variantId}`, line]));
  const removedLines = (originalDetail?.items ?? []).flatMap((line) => {
    const approved = approvedByLine.get(`${line.productId}:${line.variantId}`);
    if (!approved) return [];
    const removedSeries = Math.max(0, line.seriesQuantity - approved.approvedSeriesQuantity);
    const removedExtra = Math.max(0, line.extraQuantity - approved.approvedExtraQuantity);
    return removedSeries + removedExtra > 0 ? [{ line, removedSeries, removedExtra }] : [];
  });

  return <section className="buyer-order-detail-page">
    <div className="buyer-order-detail-heading">
      <button className="orders-back-button" onClick={props.onBack} type="button">← Geri</button>
      <h1>Sipariş Detayı</h1>
      <span className={`buyer-order-status ${props.order.status}`}>
        {buyerOrderStatusLabel(props.order.status)}
      </span>
    </div>

    {props.feedback ? <div aria-live="polite" className={`orders-page-feedback ${props.feedback.tone}`}
      role={props.feedback.tone === 'error' ? 'alert' : 'status'}>{props.feedback.text}</div> : null}

    <div className="buyer-order-detail-layout">
      <article className="buyer-order-items-card">
        <h2>Sipariş Kalemleri</h2>
        <div className="buyer-order-product-list">{groups.map((group) => <section
          className="buyer-order-product-group" key={group.productId || group.productCode}>
          <div className="buyer-order-product-heading">
            <strong>{group.productCode} - {group.productName}</strong>
            <span>({group.totalSeries} seri)</span>
          </div>
          {group.lines.filter((line) => line.seriesQuantity > 0).map((line) => <div
            className="buyer-order-color-row" key={`${line.variantId}:series`}>
            <span className="buyer-order-color-dot" style={{ backgroundColor: colorHex(props.products, line) }} />
            <strong>{line.colorName}</strong>
            <b>{line.seriesQuantity} seri</b>
          </div>)}
          {group.totalExtra > 0 ? <div className="buyer-order-extras">
            <h3>Teklemeler</h3>
            {group.lines.filter((line) => line.extraQuantity > 0).map((line) => <div
              className="buyer-order-color-row extra" key={`${line.variantId}:extra`}>
              <span className="buyer-order-color-dot" style={{ backgroundColor: colorHex(props.products, line) }} />
              <strong>{line.colorName}</strong>
              <b>{line.extraQuantity} adet</b>
            </div>)}
          </div> : null}
        </section>)}</div>
        {removedLines.length > 0 ? <section className="buyer-order-removed-card">
          <h3>Yetersiz stok nedeniyle siparişten çıkarıldı</h3>
          {removedLines.map(({ line, removedExtra, removedSeries }) => <div
            className="buyer-order-removed-row" key={`${line.productId}:${line.variantId}`}>
            <span className="buyer-order-color-dot" style={{ backgroundColor: colorHex(props.products, line) }} />
            <div><strong>{line.productCode} · {line.productName}</strong><small>{line.colorName}</small></div>
            <b>{[removedSeries > 0 ? `${removedSeries} seri` : '', removedExtra > 0
              ? `${removedExtra} adet` : ''].filter(Boolean).join(' + ')}</b>
          </div>)}
        </section> : null}
      </article>

      <aside className="buyer-order-detail-sidebar">
        <article className="buyer-order-summary-card">
          <h2>Sipariş Özeti</h2>
          <div><span><b>{groups.length}</b><small>Ürün Çeşidi</small></span>
            <span><b>{totalSeries}</b><small>Toplam Seri</small></span>
            <span><b>{totalExtra}</b><small>Tekleme</small></span></div>
        </article>
        <article className="buyer-order-information-card">
          <h2>Sipariş Bilgileri</h2>
          <dl><div><dt>Sipariş No</dt><dd>{orderNumber(props.order.orderId)}</dd></div>
            <div><dt>Sipariş Tarihi</dt><dd>{new Date(props.order.createdAt).toLocaleString('tr-TR')}</dd></div>
            <div><dt>Teslimat adresi</dt><dd>{detail.shippingAddress}</dd></div>
            {detail.note ? <div><dt>Sipariş notu</dt><dd>{detail.note}</dd></div> : null}</dl>
          {props.order.decisionDetail?.message || props.order.statusDetail?.message
            ? <p className="buyer-order-company-message">
              {props.order.decisionDetail?.message ?? props.order.statusDetail?.message}</p>
            : null}
        </article>
      </aside>
    </div>
  </section>;
}
